import { useState, useMemo, useCallback, useRef } from "react";
import * as Select from "@radix-ui/react-select";
import cx from "classnames";
import {
  getCountries,
  getCountryCallingCode,
  Country,
} from "react-phone-number-input";
import { useTranslation } from "next-i18next";

import { Input } from "components/Inputs";
import { CountryDialCode, CountryFlag, CountryName } from ".";
import { getCountryName } from "../utils";

import ChevronDown from "resources/icons/chevron-down.svg";
import Checkmark from "resources/icons/checkmark.svg";

import styles from "./CountrySelect.module.scss";

export interface CountrySelectProps {
  value: Country;
  className?: string;
  disabled?: boolean;
  onChange: (country: Country) => void;
}

export const CountrySelect = ({
  value,
  className,
  disabled = false,
  onChange,
}: CountrySelectProps) => {
  const { t } = useTranslation("translations");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const viewportRef = useRef<HTMLDivElement>(null);

  const countries = getCountries();

  const { filteredCountries, selectedMissing } = useMemo(() => {
    if (!search.trim())
      return { filteredCountries: countries, selectedMissing: false };

    const query = search.toLowerCase().trim();
    const matches = countries.filter((c) => {
      const name = getCountryName(c).toLowerCase();
      const code = c.toLowerCase();
      const dialCode = `+${getCountryCallingCode(c)}`;
      return (
        name.includes(query) || code.includes(query) || dialCode.includes(query)
      );
    });

    const selectedMissing = Boolean(value && !matches.includes(value));
    if (selectedMissing) {
      const selected = countries.find((c) => c === value);

      // Keep selected country in the list so Radix doesn't remount and steal focus
      if (selected) {
        matches.unshift(selected);
      }
    }

    return { filteredCountries: matches, selectedMissing };
  }, [countries, search, value]);

  const hasNoResults =
    filteredCountries.length === 0 ||
    (filteredCountries.length === 1 && selectedMissing);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearch("");
    }
  }, []);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();

      if (e.key === "Enter") {
        const viewport = viewportRef.current;
        const firstItem = viewport?.querySelector<HTMLElement>(
          `[role="option"]:not([data-disabled]):not(.${styles.itemHidden})`
        );

        firstItem?.dispatchEvent(
          new KeyboardEvent("keydown", {
            bubbles: true,
            cancelable: true,
            key: "Enter",
            keyCode: 13,
          })
        );
      }
    },
    []
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    []
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange(newValue as Country);
      setSearch("");
    },
    [onChange]
  );

  return (
    <Select.Root
      value={value}
      open={open}
      disabled={disabled}
      onOpenChange={handleOpenChange}
      onValueChange={handleValueChange}
    >
      <Select.Trigger className={cx(styles.trigger, className)}>
        <div className={styles.content}>
          <CountryFlag country={value} countryName={getCountryName(value)} />
          <CountryDialCode country={value} />
        </div>
        <span className={cx(styles.chevron, { [styles.chevronRotated]: open })}>
          <ChevronDown />
        </span>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className={styles.selectContent}
          position="popper"
          sideOffset={8}
          align="start"
        >
          <div className={styles.searchContainer}>
            <Input
              value={search}
              className={styles.searchInput}
              placeholder={t("", "Search country...")}
              autoFocus
              autoComplete="off"
              onKeyDown={handleSearchKeyDown}
              onChange={handleSearchChange}
            />
          </div>

          {hasNoResults && (
            <div className={styles.noResults}>
              {t("", "No countries found")}
            </div>
          )}

          <Select.Viewport ref={viewportRef} className={styles.viewport}>
            {filteredCountries.map((country) => (
              <Select.Item
                key={country}
                value={country}
                className={cx(styles.item, {
                  [styles.itemHidden]: country === value && selectedMissing,
                })}
              >
                <Select.ItemText asChild>
                  <span className={styles.itemContent}>
                    <CountryFlag
                      country={country}
                      countryName={getCountryName(country)}
                    />
                    <CountryName country={country} />
                    <CountryDialCode country={country} />
                    <Checkmark
                      className={cx(styles.indicator, {
                        [styles.visible]: value === country,
                      })}
                    />
                  </span>
                </Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

export default CountrySelect;
